import type { ErrorSource } from "../types";

export interface ParsedStackFrame {
  filename?: string;
  function?: string;
  line?: number;
  column?: number;
}

export interface ParsedStacktrace {
  message?: string;
  name?: string;
  stack?: string;
  frames: ParsedStackFrame[];
  file?: string;
  line?: number;
  column?: number;
}

const MAX_FRAMES = 50;

const chromeRegex = /\s*at (?:(.*?) )?\(?([^\s)]+?):(\d+):(\d+)\)?$/;
const firefoxSafariRegex = /^(.*?)(?:@([^@]+):(\d+):(\d+))$/;
const anonymousEvalRegex = /\(eval at .+\),/;

function normaliseFunctionName(fn?: string) {
  if (!fn) return undefined;
  const trimmed = fn.replace(/^async\s+/, "").replace(/^new\s+/, "").trim();
  if (!trimmed || trimmed === "<anonymous>") return undefined;
  return trimmed;
}

function parseLine(line: string): ParsedStackFrame | null {
  const cleaned = line.replace(anonymousEvalRegex, "").trim();
  if (!cleaned || cleaned.toLowerCase() === "native code") return null;

  let match = chromeRegex.exec(cleaned);
  if (match) {
    const [, fn, file, lineNum, colNum] = match;
    return {
      filename: file,
      function: normaliseFunctionName(fn),
      line: Number(lineNum),
      column: Number(colNum),
    };
  }

  match = firefoxSafariRegex.exec(cleaned);
  if (match) {
    const [, fn, file, lineNum, colNum] = match;
    return {
      filename: file,
      function: normaliseFunctionName(fn),
      line: Number(lineNum),
      column: Number(colNum),
    };
  }

  return null;
}

function extractMessageFromStack(stack: string | undefined) {
  if (!stack) return { name: undefined as string | undefined, message: undefined as string | undefined };
  const firstLine = stack.split("\n")[0]?.trim();
  if (!firstLine) return { name: undefined, message: undefined };
  const colonIndex = firstLine.indexOf(":");
  if (colonIndex > -1) {
    const maybeName = firstLine.slice(0, colonIndex).trim();
    const maybeMessage = firstLine.slice(colonIndex + 1).trim();
    return {
      name: maybeName || undefined,
      message: maybeMessage || undefined,
    };
  }
  return { name: undefined, message: firstLine || undefined };
}

export function parseStack(errorOrStack: unknown, source?: ErrorSource): ParsedStacktrace {
  let stackString: string | undefined;
  let message: string | undefined;
  let name: string | undefined;

  if (errorOrStack instanceof Error) {
    const err = errorOrStack;
    stackString = typeof err.stack === "string" ? err.stack : undefined;
    message = err.message || undefined;
    name = err.name || undefined;
  } else if (typeof errorOrStack === "string") {
    stackString = errorOrStack;
    if (!errorOrStack.includes("\n")) {
      message = errorOrStack;
    }
  } else if (errorOrStack && typeof errorOrStack === "object") {
    const maybeStack = (errorOrStack as any).stack;
    const maybeMessage = (errorOrStack as any).message;
    const maybeName = (errorOrStack as any).name;
    if (typeof maybeStack === "string") stackString = maybeStack;
    if (typeof maybeMessage === "string") message = maybeMessage;
    if (typeof maybeName === "string") name = maybeName;
  }

  if (!message && stackString) {
    const inferred = extractMessageFromStack(stackString);
    message = inferred.message ?? message;
    name = inferred.name ?? name;
  }

  const frames: ParsedStackFrame[] = [];
  if (stackString) {
    const lines = stackString.split("\n");
    for (let i = 0; i < lines.length && frames.length < MAX_FRAMES; i += 1) {
      const line = lines[i];
      if (i === 0 && line && message && line.includes(message)) {
        continue;
      }
      const frame = parseLine(line);
      if (frame) {
        frames.push(frame);
      }
    }
  }

  const top = frames[0];
  return {
    stack: stackString,
    message: message || (source ? `${source} error` : undefined),
    name,
    frames,
    file: top?.filename,
    line: top?.line,
    column: top?.column,
  };
}
